import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import CommentPost from 'flarum/forum/components/CommentPost';
import Icon from 'flarum/common/components/Icon';
import haptic from 'flarum/common/utils/haptic';
/**
 * Reactions show a count, never who reacted -- Task 5.5, per the decision of
 * 2026-09-18.
 *
 * The reason is stronger than the convention it follows. §4.2 grants a handle
 * for a **reaction alone**, so a list of reactors exposes people who took part
 * *without posting* -- participation that is otherwise completely invisible --
 * and builds a who-endorses-whom graph that correlates strongly inside a small
 * Issue.
 *
 * TWO THINGS BREAK WHEN THE `likes` RELATIONSHIP IS HIDDEN, and both are fixed
 * here rather than left to degrade:
 *
 *   1. `addLikesList.js` is guarded by `if (likes && likes.length)`, so the
 *      "admin likes this" line simply stops rendering. Good -- but it takes the
 *      count with it, and a count is information we chose to keep.
 *   2. `addLikeAction.js:15` decides whether YOU have liked a post by scanning
 *      that same relationship, and its click handler writes straight into
 *      `post.data.relationships.likes.data`. With the relationship gone the
 *      label is always "Like" and **the click throws**. That is a broken
 *      button, not a degraded one.
 *
 * Both now read `hasLiked`, a boolean the API answers about the asker and
 * nobody else.
 */
export default function reactions() {
  extend(CommentPost.prototype, 'actionItems', function (items) {
    const post = this.attrs.post;

    // flarum/likes absent, or the button not offered for this post.
    if (!items.has('like')) return;
    let liked = hasLiked(post);
    items.setContent('like', m(Button, {
      className: "Button Button--link",
      onclick: () => {
        liked = !liked;
        if (liked) haptic('success');
        post.save({
          isLiked: liked
        });

        /*
         * Core nudges `post.data.relationships.likes.data` by hand here so
         * the button responds before the request returns. That array no
         * longer exists, and reaching for it is what would throw. Pushing the
         * two attributes we DO serve gives the same instant feedback without
         * inventing a relationship.
         */
        post.pushAttributes({
          hasLiked: liked,
          likesCount: Math.max(0, (post.likesCount() ?? 0) + (liked ? 1 : -1))
        });
      }
    }, app.translator.trans(liked ? 'flarum-likes.forum.post.unlike_link' : 'flarum-likes.forum.post.like_link')));
  });
  extend(CommentPost.prototype, 'footerItems', function (items) {
    const post = this.attrs.post;
    const count = post.likesCount?.() ?? 0;

    /*
     * flarum/likes' own footer item is keyed `liked` (addLikesList.js:59) and
     * is guarded by `if (likes && likes.length)`, so once the relationship is
     * hidden it never gets added at all. Removing it is therefore usually a
     * no-op -- kept because "usually" is not a guarantee, and a reactor list
     * surviving here would be the leak this task exists to close.
     *
     * The count goes under our own key rather than reusing theirs, so the two
     * cannot fight over one slot whichever order they initialize in.
     */
    items.remove('liked');
    if (count < 1) {
      items.remove('ideasynqLikeCount');
      return;
    }
    const label = m("span", {
      className: "IdeaSynqLikeCount"
    }, m(Icon, {
      name: "fas fa-thumbs-up"
    }), " ", app.translator.trans('ideasynq-handles.forum.post.likes_count', {
      count
    }));
    if (items.has('ideasynqLikeCount')) {
      items.setContent('ideasynqLikeCount', label);
    } else {
      items.add('ideasynqLikeCount', label, 10);
    }
  });
}

/**
 * Whether the person asking has reacted to this post.
 *
 * Defensive because `hasLiked` is our attribute: a post model that predates it,
 * or one from another source, must read as "not liked" rather than throwing
 * inside a render.
 */
function hasLiked(post) {
  const candidate = post;
  return typeof candidate.hasLiked === 'function' ? candidate.hasLiked() === true : false;
}